import { IDelegateController, IDelegateConstant, IDelegateResponder } from 'ui-ng-delegate';
import {
  INotificationElement,
  INotificationMessage,
  IPhxNotificationScope,
  IWindowHIP,
  TNotificationStatus
} from '../types';

class PhxNotificationDelegateConfiguration {
  uniqueComponent = 'phx-notification';
  vocabulary = {
    create: this.create.bind(this),
    success: this.success.bind(this),
    info: this.info.bind(this),
    warn: this.warn.bind(this),
    error: this.error.bind(this),
    dismissAlertByName: this.dismissAlertByName.bind(this)
  };

  constructor() {}

  /**
   * Triggers HIP notification
   * @param content First argument can be a string or an object with a content property
   * @param status Second argument is optional and can be 'info', 'success', 'warn', or 'error'
   * @returns
   */
  create(
    scope: IPhxNotificationScope,
    element: HTMLElement,
    attrs: Record<string, any>,
    event: Event,
    content: INotificationMessage,
    status: TNotificationStatus
  ) {
    const fallbackStatus = 'info';
    const notificationStatus = content.status || status || fallbackStatus;
    const notificationMessage = _.isString(content) ? content : content.content;
    const notificationDelay = content.timeout || 20000;

    if (_.isUndefined(notificationMessage)) {
      return;
    }

    (window as IWindowHIP & typeof globalThis).HIP.fn
      .notify({
        status: notificationStatus,
        message: notificationMessage,
        delay: notificationDelay
      })
      .then(({ notification }: { notification: INotificationElement }) => {
        if (!notification) {
          return;
        }
        // set notification name attribute
        if (content.name) {
          notification.setAttribute('name', content.name);
        }
        scope.notifications.push(notification);
      });
    return;
  }

  success(
    scope: IPhxNotificationScope,
    element: HTMLElement,
    attrs: Record<string, any>,
    event: Event,
    messageContent: INotificationMessage
  ) {
    return this.create(scope, element, attrs, event, messageContent, 'success');
  }

  info(
    scope: IPhxNotificationScope,
    element: HTMLElement,
    attrs: Record<string, any>,
    event: Event,
    messageContent: INotificationMessage
  ) {
    return this.create(scope, element, attrs, event, messageContent, 'info');
  }

  warn(
    scope: IPhxNotificationScope,
    element: HTMLElement,
    attrs: Record<string, any>,
    event: Event,
    messageContent: INotificationMessage
  ) {
    return this.create(scope, element, attrs, event, messageContent, 'warning');
  }

  error(
    scope: IPhxNotificationScope,
    element: HTMLElement,
    attrs: Record<string, any>,
    event: Event,
    messageContent: INotificationMessage
  ) {
    return this.create(scope, element, attrs, event, messageContent, 'error');
  }

  dismissAlertByName(
    scope: IPhxNotificationScope,
    element: HTMLElement,
    attrs: Record<string, any>,
    event: Event,
    name: string
  ) {
    scope.dismissByName(name);
  }
}

class PhxNotificationController {
  constructor(private $scope: IPhxNotificationScope) {
    this.$scope = $scope;
    this.$scope.notifications = [];
    this.$scope.dismissByName = this.dismissByName.bind(this);
  }

  dismissByName(name: string) {
    const dismissIndex = this.$scope.notifications.findIndex(
      (notification) => notification.getAttribute('name') === name
    );
    if (dismissIndex < 0) {
      return;
    }
    const notificationEl = this.$scope.notifications[dismissIndex];
    notificationEl.close();

    // Remove the closed notification from the array
    this.$scope.notifications.splice(dismissIndex, 1);
  }
}

class PhxNotificationDelegate {
  constructor(DelegateController: IDelegateController, phxNotificationDelegateConfiguration: IDelegateConstant) {
    DelegateController(this, phxNotificationDelegateConfiguration);
  }
}

angular
  .module('ui-ng-notification')
  .factory('phxNotificationDelegateConfiguration', () => new PhxNotificationDelegateConfiguration())
  .controller('phxNotificationController', PhxNotificationController)
  .directive(
    'phxNotification',
    (DelegateResponder: IDelegateResponder, phxNotificationDelegateConfiguration: IDelegateConstant) => ({
      replace: false,
      restrict: 'E',
      scope: {},
      controller: 'phxNotificationController',
      link: DelegateResponder(phxNotificationDelegateConfiguration, () => {})
    })
  )
  .service('phxNotificationDelegate', PhxNotificationDelegate);



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-notification/src/core/notification_delegate.ts